(() => {
  const GLOBAL_KEY = '__jdAnimatedImageCollageController';
  const SECTION_SELECTOR = '.aic-section';
  const VERTICAL_SELECTOR = '.aic__marquee';
  const HORIZONTAL_SELECTOR = '.aic__marquee-horizontal';
  const TRACK_CLASS = 'aic__native-marquee-track';
  const GROUP_CLASS = 'aic__native-marquee-group';
  const motionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init();
    return;
  }

  const statesBySection = new WeakMap();
  const activeStates = new Set();

  const getSections = (root = document) => {
    const sections = [];
    const closestSection = root.closest?.(SECTION_SELECTOR);

    if (closestSection) sections.push(closestSection);

    if (root.matches?.(SECTION_SELECTOR) && !sections.includes(root)) {
      sections.push(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      if (!sections.includes(section)) sections.push(section);
    });

    return sections;
  };

  const getGap = (marquee) => {
    const computedGap = window.getComputedStyle?.(marquee).gap;

    if (computedGap && computedGap !== 'normal') return computedGap;
    return window.innerWidth >= 1200 ? '24px' : '8px';
  };

  const sanitizeClone = (clone) => {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('id');

    clone.querySelectorAll('[id]').forEach((element) => {
      element.removeAttribute('id');
    });

    clone
      .querySelectorAll('[data-shopify-editor-block], [data-shopify-editor-section]')
      .forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('data-shopify-editor-section');
      });

    clone.querySelectorAll('style').forEach((style) => style.remove());

    clone
      .querySelectorAll(
        'a, button, input, select, textarea, summary, [tabindex]',
      )
      .forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
  };

  const createTrack = (marquee, horizontal) => {
    const originalChildren = Array.from(marquee.children);
    if (!originalChildren.length) return null;

    const gap = getGap(marquee);
    const track = document.createElement('div');
    const primaryGroup = document.createElement('div');

    track.className = TRACK_CLASS;
    primaryGroup.className = GROUP_CLASS;

    track.style.display = 'flex';
    track.style.flexDirection = horizontal ? 'row' : 'column';
    track.style.width = horizontal ? 'max-content' : '100%';
    track.style.alignItems = horizontal ? 'flex-end' : 'stretch';

    primaryGroup.style.display = 'flex';
    primaryGroup.style.flexDirection = horizontal ? 'row' : 'column';
    primaryGroup.style.alignItems = horizontal ? 'flex-end' : 'stretch';
    primaryGroup.style.flex = 'none';
    primaryGroup.style.gap = gap;

    if (horizontal) primaryGroup.style.paddingRight = gap;
    else primaryGroup.style.paddingBottom = gap;

    originalChildren.forEach((child) => primaryGroup.appendChild(child));

    const duplicateGroup = primaryGroup.cloneNode(true);
    sanitizeClone(duplicateGroup);

    track.append(primaryGroup, duplicateGroup);
    marquee.replaceChildren(track);
    marquee.dataset.aicMarqueeReady = 'true';
    marquee.style.display = 'block';
    marquee.style.overflow = 'hidden';

    return { track, primaryGroup, duplicateGroup };
  };

  const createAnimation = (state, trackData, options) => {
    const { track, primaryGroup } = trackData;
    const { direction, duration, horizontal } = options;

    if (typeof track.animate !== 'function') return null;

    const keyframes =
      direction === 'forward'
        ? [
            { transform: `translate3d(0, 0, 0)` },
            {
              transform: horizontal
                ? 'translate3d(-50%, 0, 0)'
                : 'translate3d(0, -50%, 0)',
            },
          ]
        : [
            {
              transform: horizontal
                ? 'translate3d(-50%, 0, 0)'
                : 'translate3d(0, -50%, 0)',
            },
            { transform: `translate3d(0, 0, 0)` },
          ];

    const animation = track.animate(keyframes, {
      duration,
      iterations: Infinity,
      easing: 'linear',
      fill: 'both',
    });

    animation.pause();
    animation.currentTime = 0;

    const animationState = {
      animation,
      track,
      primaryGroup,
      horizontal,
    };

    state.animations.push(animationState);
    return animationState;
  };

  const updateHorizontalDuration = (animationState, section) => {
    const width =
      animationState.primaryGroup.getBoundingClientRect().width ||
      animationState.primaryGroup.scrollWidth;
    const sectionWidth =
      section.getBoundingClientRect().width ||
      document.documentElement.clientWidth ||
      window.innerWidth;
    const speed = Math.max(
      1,
      sectionWidth > 575
        ? Math.round(sectionWidth * 0.03)
        : Math.round(sectionWidth * 0.08),
    );
    const duration = Math.max(1, Math.round((width / speed) * 1000));

    animationState.animation.effect?.updateTiming?.({ duration });
  };

  const setPlayback = (state) => {
    const shouldPlay =
      state.inView && !document.hidden && !Boolean(motionQuery?.matches);

    state.animations.forEach(({ animation, track }) => {
      track.style.willChange = shouldPlay ? 'transform' : 'auto';

      if (shouldPlay) animation.play();
      else animation.pause();
    });
  };

  const loadSectionImages = (state) => {
    if (state.imagesLoaded) return;

    state.section.querySelectorAll('.aic__slide img').forEach((image) => {
      image.removeAttribute('loading');
    });

    state.imagesLoaded = true;
  };

  const setupVerticalMarquees = (state) => {
    const isRightLayout = Boolean(state.section.querySelector('.aic--right'));
    const duration = isRightLayout ? 40000 : 50000;

    state.section.querySelectorAll(VERTICAL_SELECTOR).forEach((marquee, index) => {
      if (marquee.dataset.aicMarqueeReady === 'true') return;

      const trackData = createTrack(marquee, false);
      if (!trackData) return;

      const movesDown = index % 2 === 0;
      marquee.classList.add(movesDown ? 'marquee-down' : 'marquee-up');

      createAnimation(state, trackData, {
        direction: movesDown ? 'reverse' : 'forward',
        duration,
        horizontal: false,
      });
    });
  };

  const setupHorizontalMarquee = (state) => {
    const marquee = state.section.querySelector(HORIZONTAL_SELECTOR);
    if (!marquee || marquee.dataset.aicMarqueeReady === 'true') return;

    const trackData = createTrack(marquee, true);
    if (!trackData) return;

    const animationState = createAnimation(state, trackData, {
      direction: 'forward',
      duration: 50000,
      horizontal: true,
    });

    if (!animationState) return;

    const updateDuration = () => {
      updateHorizontalDuration(animationState, state.section);
    };

    updateDuration();

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(updateDuration);
      resizeObserver.observe(trackData.primaryGroup);
      state.cleanup.push(() => resizeObserver.disconnect());
    } else {
      let resizeFrame = null;
      const onResize = () => {
        if (resizeFrame) return;

        resizeFrame = window.requestAnimationFrame(() => {
          resizeFrame = null;
          updateDuration();
        });
      };

      window.addEventListener('resize', onResize, { passive: true });
      state.cleanup.push(() => {
        window.removeEventListener('resize', onResize);
        if (resizeFrame) window.cancelAnimationFrame?.(resizeFrame);
      });
    }
  };

  const initializeSection = (section) => {
    if (!section || statesBySection.has(section)) return;

    const state = {
      section,
      animations: [],
      cleanup: [],
      imagesLoaded: false,
      inView: false,
    };

    statesBySection.set(section, state);
    activeStates.add(state);

    setupVerticalMarquees(state);
    setupHorizontalMarquee(state);

    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target !== section) return;

            state.inView = entry.isIntersecting;
            if (entry.isIntersecting) loadSectionImages(state);
            setPlayback(state);
          });
        },
        {
          root: null,
          rootMargin: '400px 0px',
          threshold: 0.01,
        },
      );

      observer.observe(section);
      state.cleanup.push(() => observer.disconnect());
    } else {
      state.inView = true;
      loadSectionImages(state);
      setPlayback(state);
    }
  };

  const destroySection = (section) => {
    const state = statesBySection.get(section);
    if (!state) return;

    state.cleanup.forEach((cleanup) => cleanup());
    state.animations.forEach(({ animation, track }) => {
      animation.cancel();
      track.style.willChange = 'auto';
    });

    activeStates.delete(state);
    statesBySection.delete(section);
  };

  const init = (root = document) => {
    getSections(root).forEach(initializeSection);
  };

  const destroy = (root) => {
    getSections(root).forEach(destroySection);
  };

  const refreshPlayback = () => {
    activeStates.forEach(setPlayback);
  };

  const controller = { init, destroy };
  window[GLOBAL_KEY] = controller;

  document.addEventListener('visibilitychange', refreshPlayback);
  motionQuery?.addEventListener?.('change', refreshPlayback);

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    init(event.target);
  });

  init();
})();
